import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, Download, Mail } from "lucide-react";
import { EyeMark } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Particle = ({ i }) => {
  const angle = (i / 24) * Math.PI * 2;
  const dist = 120 + (i % 5) * 40;
  return (
    <motion.span
      className="absolute left-1/2 top-1/2 w-1.5 h-1.5 rounded-full bg-[#D4AF37]"
      initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
      animate={{ x: Math.cos(angle) * dist, y: Math.sin(angle) * dist, opacity: 0, scale: 0.2 }}
      transition={{ duration: 1.6, delay: 0.3 + i * 0.03, ease: "easeOut" }}
      aria-hidden="true"
    />
  );
};

const Success = () => {
  const [params] = useSearchParams();
  const orderId = params.get("order");
  const [order, setOrder] = useState(null);
  const [failed, setFailed] = useState(false);
  const particles = useMemo(() => Array.from({ length: 24 }, (_, i) => i), []);

  useEffect(() => {
    if (!orderId) {
      setFailed(true);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/orders/${orderId}`);
        if (!cancelled) {
          if (data.status === "paid") setOrder(data);
          else setTimeout(load, 1500);
        }
      } catch {
        if (!cancelled) setFailed(true);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  if (failed) {
    return (
      <div className="min-h-screen bg-[#052730] flex flex-col items-center justify-center gap-6 px-4 text-center" data-testid="success-error">
        <p className="font-serif-alt italic text-2xl text-[#94A3B8]">We couldn't find that order.</p>
        <Link to="/" className="text-[#FCD34D] underline underline-offset-4" data-testid="success-error-home-link">
          Return to the Archive
        </Link>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-[#052730] flex items-center justify-center" data-testid="success-loading">
        <p className="font-serif-alt italic text-2xl text-[#94A3B8] animate-pulse">Confirming your payment…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen grain bg-[#052730] flex items-center justify-center px-4 py-16" data-testid="success-page">
      <div className="relative max-w-lg w-full text-center">
        <div className="absolute inset-0 pointer-events-none">
          {particles.map((i) => (
            <Particle key={i} i={i} />
          ))}
        </div>
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative rounded-xl border border-[#D4AF37]/25 bg-[#06333E] p-8 sm:p-12 gold-glow"
        >
          <div className="flex justify-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", delay: 0.2, damping: 12 }}
            >
              <CheckCircle2 className="w-14 h-14 text-[#D4AF37]" data-testid="success-icon" />
            </motion.div>
          </div>
          <h1 className="mt-6 font-display text-3xl sm:text-4xl font-bold text-[#F8FAFC]">The Archive is yours</h1>
          <p className="mt-3 font-serif-alt italic text-lg text-amber-200/80">
            "A signal is simply something that asks to be noticed."
          </p>

          <a
            data-testid="success-download-button"
            href={`${API}/orders/${orderId}/download`}
            className="mt-8 inline-flex w-full items-center justify-center gap-3 rounded-full bg-[#D4AF37] py-4 text-lg font-semibold text-[#052730] transition-all duration-300 hover:bg-[#FCD34D] hover:shadow-[0_0_40px_rgba(212,175,55,0.45)] active:scale-[0.98]"
          >
            <Download className="w-5 h-5" /> Download the Ebook (PDF)
          </a>

          <div className="mt-6 flex items-center justify-center gap-2 text-sm text-[#94A3B8]" data-testid="success-email-status">
            <Mail className="w-4 h-4 text-[#D4AF37]" />
            {order.email_sent
              ? "A copy of your download link was sent to your email."
              : "Your download link email is on its way — check your inbox shortly."}
          </div>

          <div className="mt-8 border-t border-[#D4AF37]/15 pt-6 text-xs text-[#94A3B8] space-y-1">
            <p data-testid="success-order-id">Order {order.order_id}</p>
            <p>
              {order.title} — ${order.amount.toFixed(2)} {order.currency.toUpperCase()}
            </p>
          </div>

          <Link
            to="/"
            className="mt-6 inline-block text-sm text-[#FCD34D] underline underline-offset-4 hover:text-[#D4AF37]"
            data-testid="success-home-link"
          >
            Return to the Archive
          </Link>
        </motion.div>
        <div className="mt-8 flex justify-center opacity-60">
          <EyeMark className="w-8 h-8" />
        </div>
      </div>
    </div>
  );
};

export default Success;
